/**
 * Collect author user IDs and pre-resolved author rows from ProseMirror JSON.
 * Handles author marks, hashed overlapping mark names (author--*), and userMention nodes.
 */

function addUid(set, value) {
    if (value === undefined || value === null || value === '') return;
    set.add(String(value));
}

function collectFromMarks(marks, userIds, preResolved, seenUids) {
    if (!Array.isArray(marks)) return;

    for (const mark of marks) {
        const type = mark?.type || '';
        if (!type.startsWith('author')) continue;

        const attrs = mark.attrs || {};
        const uid = attrs.userId ?? attrs.user ?? attrs.id;
        if (!uid) continue;

        const uidStr = String(uid);
        userIds.add(uidStr);

        if ((attrs.displayName || attrs.name) && !seenUids.has(uidStr)) {
            seenUids.add(uidStr);
            preResolved.push({
                uid: uidStr,
                displayName: attrs.displayName || attrs.name,
                headshot: attrs.headshot || attrs.photoURL || null,
            });
        }
    }
}

function traverseNode(node, userIds, preResolved, seenUids) {
    if (!node || typeof node !== 'object') return;

    if (node.type === 'userMention' && node.attrs) {
        const uid = node.attrs.id ?? node.attrs.uid ?? node.attrs.userId;
        if (uid) {
            const uidStr = String(uid);
            userIds.add(uidStr);
            if (!seenUids.has(uidStr)) {
                seenUids.add(uidStr);
                preResolved.push({
                    uid: uidStr,
                    displayName: node.attrs.name || node.attrs.label || uidStr,
                    headshot: node.attrs.headshot || null,
                });
            }
        }
    }

    const attrs = node.attrs;
    if (attrs) {
        addUid(userIds, attrs['data-author']);
        addUid(userIds, attrs.userId);
        addUid(userIds, attrs.user);
    }

    collectFromMarks(node.marks, userIds, preResolved, seenUids);

    if (Array.isArray(node.content)) {
        node.content.forEach((child) => traverseNode(child, userIds, preResolved, seenUids));
    }
}

/**
 * @param {object|null|undefined} pmJson
 * @returns {string[]}
 */
export function extractAuthorUserIdsFromPmJson(pmJson) {
    return collectAuthorsFromPmJson(pmJson).userIds;
}

/**
 * @param {object|null|undefined} pmJson
 * @returns {{ userIds: string[], preResolved: Array<{uid: string, displayName: string, headshot?: string|null}> }}
 */
export function collectAuthorsFromPmJson(pmJson) {
    const userIds = new Set();
    const preResolved = [];
    const seenUids = new Set();

    if (!pmJson) {
        return {userIds: [], preResolved: []};
    }

    if (pmJson.type === 'doc' && Array.isArray(pmJson.content)) {
        pmJson.content.forEach((child) => traverseNode(child, userIds, preResolved, seenUids));
    } else {
        traverseNode(pmJson, userIds, preResolved, seenUids);
    }

    return {
        userIds: Array.from(userIds),
        preResolved,
    };
}
